/**
 * parallelMenu.jsx
 *
 * @Author: zhangce
 * @Date: 17/12/29 下午5:13
 */

import React, { Children, isValidElement, cloneElement } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Menu from './Menu';
import MenuItem from './MenuItem';
import SubMenu from './SubMenu';
import Rolling from '../icons/internals/Rolling';
import { isEqualArrays } from '../util/isEqual';
import { arrayTreeFilter } from '../util/arrayTreeFilter';
/**
 * ParallelMenu
 * @description 用于级联组件
 */
class ParallelMenu extends React.PureComponent {
  static propTypes = {
    /**
     * 类前缀
     * */
    prefixCls: PropTypes.string,
    /**
     * 类名
     * */
    className: PropTypes.string,
    /**
     * 默认值
     * */
    defaultValue: PropTypes.array,
    /**
     * 树结构值
     * */
    options: PropTypes.array,
    /**
     * 选中回调
     * */
    onSelect: PropTypes.func,
    /**
     * 改变值回调
     * */
    onChange: PropTypes.func,
    /**
     * 选即变
     * */
    changeOnSelect: PropTypes.bool,
    /**
     * options子元素key值
     * */
    childrenKeyName: PropTypes.string,
  };

  static defaultProps = {
    prefixCls: 'ne-parallel-menu',
    defaultValue: [],
    changeOnSelect: false,
    childrenKeyName: 'children',
  };

  static displayName = 'NeatUI.ParallelMenu';

  constructor(props) {
    super(props);
    this.currentValue = [...props.defaultValue];
    this.state = {
      value: props.defaultValue,
    };
  }

  componentWillReceiveProps(newProps) {
    if (newProps.defaultValue !== this.props.defaultValue) {
      this.setState({
        value: newProps.defaultValue,
      });
      this.currentValue = [...newProps.defaultValue];
    }
  }

  // 判断元素为subMenu
  isSubMenu = ele => ele.type.isSubMenu;

  // 判断元素为menuItem
  isMenuItem = ele => ele.type.isMenuItem;

  getNewValue = (i, val, array) => {
    const newArr = array.slice(0);
    newArr[i] = val;
    return newArr.slice(0, i + 1);
  };

  onChange = i => (e, val) => {
    const { onChange, changeOnSelect, defaultValue } = this.props;
    const isLast = this.isLast;
    this.currentValue = this.getNewValue(i, val, this.currentValue);
    if (!changeOnSelect && isLast && !isEqualArrays(this.currentValue, defaultValue)) {
      onChange && onChange(this.currentValue, this.isLast);
    }
  };

  onSelect = i => (e, val) => {
    const { value } = this.state;
    const { onSelect, onChange, changeOnSelect, defaultValue } = this.props;
    const isLast = this.isLast;
    const newValue = this.getNewValue(i, val, value);
    onSelect && onSelect(e, val, isLast, i, newValue);
    this.setState({
      value: newValue,
    });
    if (changeOnSelect && !isEqualArrays(newValue, defaultValue)) {
      this.currentValue = [...newValue];
      onChange && onChange(this.currentValue, this.isLast);
    }
  };

  // 为menu的每项item添加onClick 判断当前点击元素是否为最后一级
  getMenuItems(children) {
    const items = [];
    Children.forEach(children, item => {
      if (isValidElement(item)) {
        items.push(
          cloneElement(item, {
            onClick: () => (this.isLast = !this.isSubMenu(item)),
          }),
        );
      }
    });
    return items;
  }

  // 获取有效的子组件SubMenu/MenuItem
  getValidChildren = children => {
    let result;
    if (Array.isArray(children)) {
      result = children.filter(o => this.isSubMenu(o) || this.isMenuItem(o));
    } else if (
      isValidElement(children) &&
      (this.isSubMenu(children) || this.isMenuItem(children))
    ) {
      result = [children];
    } else {
      result = [];
    }
    return result;
  };

  // 获取选中项
  getActiveMenuItems(values) {
    const { childrenKeyName } = this.props;
    let { value } = this.state;
    value = values || value;
    const option = this.childrenEle;
    return arrayTreeFilter(
      option,
      (o, level) => o.props.value === value[level],
      obj => this.getValidChildren(obj.props[childrenKeyName]),
      { childrenKeyName },
    );
  }

  // options递归转换为树形结构组件
  renderChildren = children => {
    const { childrenKeyName, prefixCls } = this.props;
    return children.map((ele, i) => {
      if (ele[childrenKeyName]) {
        return (
          <SubMenu
            key={i}
            mode="parallel"
            value={ele.value}
            title={ele.label}
            disabled={ele.disabled}>
            {ele[childrenKeyName] && this.renderChildren(ele[childrenKeyName])}
          </SubMenu>
        );
      }
      return (
        <MenuItem
          key={i}
          value={ele.value}
          disabled={ele.disabled}
          suffix={
            ele.loading && (ele.loadingIcon || <Rolling className={`${prefixCls}-loading`} spin />)
          }>
          {ele.label}
        </MenuItem>
      );
    });
  };

  // 渲染的menus
  getShowMenus() {
    const { children, options } = this.props;
    this.childrenEle = options ? this.renderChildren(options) : children;
    const result = this.getActiveMenuItems()
      .map(
        activeOption =>
          this.getValidChildren(activeOption.props.children).length && activeOption.props.children,
      )
      .filter(activeOption => !!activeOption);
    result.unshift(this.childrenEle);
    return result;
  }

  // 渲染menu 绑定onClick事件 判断是否为最后一个元素
  renderSubMenuItems = ele => {
    let menu;
    if (Array.isArray(ele) && ele.length) {
      menu = this.getMenuItems(ele);
    } else if (isValidElement(ele)) {
      menu = this.getMenuItems([ele]);
    } else {
      menu = <MenuItem disabled>暂无数据</MenuItem>;
    }
    return menu;
  };

  render() {
    const {
      prefixCls,
      className,
      defaultMenu,
      options,
      defaultValue,
      onSelect,
      onChange,
      changeOnSelect,
      childrenKeyName,
      ...other
    } = this.props;
    const { value } = this.state;
    const classString = classNames(prefixCls, className);
    return (
      <div className={classString}>
        {this.getShowMenus().map((ele, i) => (
          <Menu
            key={i}
            className={`${prefixCls}-submenu`}
            selectable
            defaultValue={value[i]}
            onChange={this.onChange(i)}
            onSelect={this.onSelect(i)}
            {...other}>
            {this.renderSubMenuItems(ele)}
          </Menu>
        ))}
      </div>
    );
  }
}

export default ParallelMenu;



// WEBPACK FOOTER //
// ./src/menu/ParallelMenu.jsx